// Pieces used by Overview, Send and Send history.
import { Check, ClockCounterClockwise, Copy, PaperPlaneTilt, User } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Ic, useTick } from "@/components/bits";
import { dur, fmt, whenLabel } from "@/lib/format";
import { SENDER_BADGE, senderState } from "@/model/advice";
import type { Person } from "@/model/people";
import { renderParts, templateBody, varsFor } from "@/model/templates";
import { useApp } from "@/shell/appData";
import { copyText, useToast } from "@/shell/toast";

/** Big number that counts up once on mount (prototype countUp). `data-count` holds the final value. */
export function CountUp({ n }: { n: number }) {
  const [v, setV] = useState<number | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    started.current = true;
    const to = n;
    const t0 = performance.now();
    let raf = 0;
    let finished = false;
    const f = (t: number) => {
      const p = Math.min(1, (t - t0) / 1000);
      finished = p >= 1;
      setV(finished ? null : Math.round(to * (1 - (1 - p) ** 4)));
      if (!finished) raf = requestAnimationFrame(f);
    };
    raf = requestAnimationFrame(f);
    return () => {
      cancelAnimationFrame(raf);
      // interrupted (StrictMode remount, or n changed mid-count): allow one more run
      if (!finished) started.current = false;
      setV(null);
    };
  }, [n]);
  // live updates after the first count show the real value straight away
  return <b data-count={n}>{fmt(v ?? n)}</b>;
}

/** Sender strip (prototype senderLine). Read-only in v1: no pause/resume. */
export function SenderStrip() {
  const { runs, runRows, cap, next, counts } = useApp();
  const now = useTick(15_000);
  const st = senderState(runs, cap, next.waiting, now, counts.people);
  const live = runRows.find((r) => r.state === "running");
  const b = SENDER_BADGE[st];
  let text: React.ReactNode;
  let cta: React.ReactNode = null;
  if (st === "running" && live) {
    text = (
      <>
        <b>{live.label}</b> is going out · {fmt(live.people)} phones so far · started by {live.run.by || "the CLI"}
      </>
    );
    cta = (
      <Link className="btn line" to="/history">
        <Ic i={ClockCounterClockwise} />
        History
      </Link>
    );
  } else if (st === "capped") {
    text = (
      <>
        Today’s {fmt(cap.cap)} are used. The next batch can start{" "}
        <b>{cap.freesAt ? whenLabel(cap.freesAt, now) : "later"}</b>
        {cap.freesAt ? <span className="num"> (in {dur(cap.freesAt - now)})</span> : null}.
      </>
    );
    cta = (
      <Link className="btn line" to="/send">
        See next batch
      </Link>
    );
  } else if (st === "ready") {
    text = (
      <>
        <b>{fmt(next.phones)} people</b> can go now under today’s cap.
      </>
    );
    cta = (
      <Link className="btn primary" to="/send">
        <Ic i={PaperPlaneTilt} />
        Review next batch
      </Link>
    );
  } else if (st === "empty") {
    text = <>No one is on the list yet. Run `wa publish` to load the sheet.</>;
  } else {
    text = <>Everyone on the list has been messaged. New sign-ups show up after the next `wa publish`.</>;
  }
  return (
    <div className="strip" data-testid="sender-strip">
      <span className="st">
        <span className={`sdot ${b.cls}`} />
        {b.label}
      </span>
      <span className="sd">{text}</span>
      {cta}
    </div>
  );
}

/** The template rendered for a sample person (first in line). */
export function SampleBubble({ p, label }: { p: Person | undefined; label: string }) {
  const { config } = useApp();
  const reg = p?.regs.find((r) => !r.ledger?.wamid) ?? p?.regs[0];
  const body = templateBody(config?.template, config);
  return (
    <div className="chat">
      <div className="bub out" style={{ maxWidth: "100%" }}>
        <div className="tg">
          <Ic i={User} />
          {label}
        </div>
        {body ? (
          renderParts(body, reg ? varsFor(reg, config) : {}).map((x, i) =>
            x.v ? (
              <span key={i} className="var">
                {x.text}
              </span>
            ) : (
              <span key={i}>{x.text}</span>
            ),
          )
        ) : (
          <span className="muted">Template text not published yet. Run `wa publish`.</span>
        )}
      </div>
    </div>
  );
}

/** CLI command with a copy button (Send is read-only in v1). */
export function CliCommand({ cmd }: { cmd: string }) {
  const toast = useToast();
  const [ok, setOk] = useState(false);
  return (
    <div className="cli">
      <code data-testid="cli-command">{cmd}</code>
      <button
        type="button"
        className="btn line xs"
        aria-label="Copy command"
        onClick={() =>
          copyText(cmd).then((y) => {
            setOk(y);
            toast(y ? "Command copied. Run it in a terminal." : "Could not copy. Select the text instead.");
            setTimeout(() => setOk(false), 1600);
          })
        }
      >
        <Ic i={ok ? Check : Copy} />
        {ok ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
